import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { isEqual, cloneDeep } from 'lodash';

interface RegisteredForm {
	getFormMethods: () => UseFormReturn<any>;
	initValues: object;
	notMonitoredValues?: string[];
}

interface RegisteredFormByKey {
	key: string;
	getFormMethods: () => UseFormReturn<any>;
	initValues: object;
	notMonitoredValues?: string[];
}

export interface ModalContextProps {
	registerForm: (
		getFormMethods: () => UseFormReturn<any>,
		notMonitoredValues?: string[],
	) => void;
	unregisterForm: (getFormMethods: () => UseFormReturn<any>) => void;
	registerFormByKey: (
		key: string,
		getFormMethods: () => UseFormReturn<any>,
		notMonitoredValues?: string[],
	) => void;
	checkIfModalCanBeClosed: () => boolean;
	checkIfModalCanBeClosedByKeys: (keys: string[]) => boolean;
	onModalClose: () => void;
	setModalProperties: ({}: ModelProperties) => void;
	unregisterFormsByKey: (keys: string[]) => void;
}

export interface ModalContextProviderProps {
	values?: ModalContextProps;
	children?: React.ReactChild | React.ReactChild[];
}

export interface ModelProperties {
	onModalClose?: () => void;
}

export const ModalContext = React.createContext<ModalContextProps>({
	registerForm: () => {},
	registerFormByKey: () => {},
	checkIfModalCanBeClosed: () => false,
	unregisterForm: () => {},
	checkIfModalCanBeClosedByKeys: () => false,
	onModalClose: () => {},
	setModalProperties: () => {},
	unregisterFormsByKey: () => {},
});

export const useModalContext: () => ModalContextProps = () => {
	return React.useContext<ModalContextProps>(ModalContext);
};

export function ModalContextProvider({
	values,
	children,
}: ModalContextProviderProps) {
	const [registeredForms, setRegisteredForms] = useState<
		RegisteredForm[] | undefined
	>(undefined);

	const [registeredFormsByKey, setRegisteredFormsByKey] = useState<
		RegisteredFormByKey[] | undefined
	>(undefined);
	const [modalProperties, setModalProperties] = useState<ModelProperties>({});

	const registerForm = (
		getFormMethods: () => UseFormReturn<any>,
		notMonitoredValues?: string[],
	) => {
		if (getFormMethods) {
			const newForm: RegisteredForm = {
				getFormMethods,
				initValues: cloneDeep(getFormMethods()?.getValues()),
				notMonitoredValues,
			};
			setRegisteredForms(prevState => {
				if (prevState) {
					return [...prevState, ...[newForm]];
				}
				return [...[newForm]];
			});
		}
	};

	const unregisterForm = (getFormMethods: () => UseFormReturn<any>) => {
		setRegisteredForms(prevState =>
			prevState?.filter(item => item.getFormMethods() !== getFormMethods()),
		);
	};

	const registerFormByKey = (
		key: string,
		getFormMethods: () => UseFormReturn<any>,
		notMonitoredValues?: string[],
	) => {
		if (getFormMethods) {
			setRegisteredFormsByKey(prevState => {
				const filteredPrevState = prevState?.filter(item => item.key !== key);
				const newForm: RegisteredFormByKey = {
					key,
					getFormMethods,
					initValues: cloneDeep(getFormMethods()?.getValues()),
					notMonitoredValues,
				};
				if (filteredPrevState) {
					return [...filteredPrevState, ...[newForm]];
				}
				return [...[newForm]];
			});
		}
	};

	const checkIfModalCanBeClosed = () => {
		const somethingInProgress = registeredForms?.some(item => {
			const formValues = item.getFormMethods?.()?.getValues();
			const initValues = item.initValues;
			console.log('formValues auto', formValues);
			console.log('initValues auto', initValues);
			if (item.notMonitoredValues?.length) {
				item.notMonitoredValues?.forEach((key: string) => {
					recursiveDelete(formValues, key.split('.'));
					recursiveDelete(initValues, key.split('.'));
				});
			}
			return !isEqual(formValues, initValues);
		});
		return !somethingInProgress;
	};

	const checkIfModalCanBeClosedByKeys = (keys: string[]) => {
		const forms = registeredFormsByKey?.filter(item =>
			keys.some(key => key === item.key),
		);

		const somethingInProgress = forms?.some(item => {
			const formValues = item.getFormMethods?.()?.getValues();
			const initValues = item.initValues;
			console.log('formValues by keys', formValues);
			console.log('initValues by keys', initValues);
			if (!formValues) {
				return true;
			}
			if (item.notMonitoredValues?.length) {
				item.notMonitoredValues?.forEach((key: string) => {
					recursiveDelete(formValues, key.split('.'));
					recursiveDelete(initValues, key.split('.'));
				});
			}
			return !isEqual(formValues, initValues);
		});
		return !somethingInProgress;
	};

	const recursiveDelete = (item: object, keys: string[]) => {
		let i: any = item;
		for (const inx in keys) {
			const index = parseInt(inx);
			if (index === keys.length - 1) {
				delete i[keys[index]];
			} else {
				i = i[keys[index]];
			}
		}
	};

	const unregisterFormsByKey = (keys: string[]) => {
		setRegisteredFormsByKey(prevState =>
			prevState?.filter(item => !keys.some(key => key === item.key)),
		);
	};

	return (
		<ModalContext.Provider
			value={{
				registerForm,
				registerFormByKey,
				checkIfModalCanBeClosed,
				unregisterForm,
				checkIfModalCanBeClosedByKeys,
				onModalClose: () => modalProperties?.onModalClose?.(),
				setModalProperties,
				unregisterFormsByKey,
			}}>
			{children}
		</ModalContext.Provider>
	);
}
